import { cn } from "@/lib/utils"
import type { Blueprint } from "@/types/exams"

/** How far (in percentage points) an area can drift from its target and still count as balanced. */
const TOLERANCE = 5

export function BlueprintCheck({ blueprint }: { blueprint: Blueprint }) {
  if (blueprint.total === 0) {
    return <p className="mt-2">Add tagged questions to see how this exam compares with the blueprint.</p>
  }

  return (
    <>
      <ul className="mt-4 divide-y divide-hairline overflow-hidden rounded-xl border border-hairline bg-page p-0">
        {blueprint.areas.map((area) => {
          const diff = area.target == null ? null : area.percent - area.target
          const balanced = diff != null && Math.abs(diff) <= TOLERANCE
          return (
            <li key={area.id} className="list-none px-4 py-3">
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <span className="min-w-0 text-sm text-ink-display">{area.name}</span>
                <span
                  className={cn(
                    "text-sm",
                    diff == null ? "text-ink-muted" : balanced ? "text-accent-display" : "text-signal-display",
                  )}
                >
                  {area.target == null
                    ? `${area.count} tagged · covered through case scenarios`
                    : `${area.percent}% of ${area.target}% target · ${area.count} ${area.count === 1 ? "question" : "questions"}` +
                      (balanced ? "" : diff! > 0 ? ` · over by ${diff}` : ` · under by ${-diff!}`)}
                </span>
              </div>
              {area.target != null && (
                <div
                  className="relative mt-2 h-1.5 rounded-full bg-surface"
                  role="meter"
                  aria-label={`${area.name}: ${area.percent}% of questions, target ${area.target}%`}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={area.percent}
                >
                  <div
                    className={cn("h-full rounded-full", balanced ? "bg-accent" : "bg-signal")}
                    style={{ width: `${Math.min(area.percent, 100)}%` }}
                  />
                  <div
                    className="absolute -top-1 h-3.5 w-0.5 bg-ink-display"
                    style={{ left: `${area.target}%` }}
                    aria-hidden
                    title={`Target ${area.target}%`}
                  />
                </div>
              )}
            </li>
          )
        })}
      </ul>
      <p className="mt-2 text-xs text-ink-muted">
        {blueprint.total} questions in total. The dark tick marks each area's target share.
        {blueprint.untagged > 0 && ` ${blueprint.untagged} untagged ${blueprint.untagged === 1 ? "question isn't" : "questions aren't"} counted toward any area.`}
      </p>
    </>
  )
}
